import { platformLabel } from '../lib/format';

/**
 * The platform a post came from, as a mark.
 *
 * The card said "Reddit" in 13px muted type in the middle of a meta line made entirely of 13px
 * muted type, so which platform a draft belonged to was a word to be found rather than something
 * to be seen - and the queue deliberately mixes Reddit, Facebook and Instagram in one list. A mark
 * in front of the group name answers the question at a glance and gives the eye a shape to aim at
 * when scanning.
 *
 * Inline SVG rather than an image or an icon package: no network request, no dependency to keep
 * up to date, no flash of missing icon, and every mark is a handful of primitives that can be
 * read and adjusted right here.
 *
 * These are simplified versions of the platforms' own marks, used only to identify where a post
 * came from. They are their owners' trademarks, so they stay recognisable rather than restyled to
 * match this theme - the colour is the point.
 */

interface Props {
  platform: string | null | undefined;
  size?: number;
}

export default function PlatformIcon({ platform, size = 16 }: Props) {
  const label = platformLabel(platform);

  return (
    <svg
      className="platform-icon"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      role="img"
      aria-label={label}
    >
      <title>{label}</title>
      {glyph(platform ?? '')}
    </svg>
  );
}

function glyph(platform: string) {
  switch (platform) {
    case 'reddit':
      // Snoo in a disc: antenna ball, head, two eyes, a smile.
      return (
        <>
          <circle cx="12" cy="12" r="11" fill="#FF4500" />
          <path d="M12 6.4V4.3" stroke="#fff" strokeWidth="1.3" strokeLinecap="round" />
          <circle cx="12" cy="3.3" r="1.15" fill="#fff" />
          <circle cx="12" cy="12.4" r="6.1" fill="#fff" />
          <circle cx="9.7" cy="11.4" r="1.25" fill="#FF4500" />
          <circle cx="14.3" cy="11.4" r="1.25" fill="#FF4500" />
          <path
            d="M9.7 14.6a3.3 3.3 0 0 0 4.6 0"
            stroke="#FF4500"
            strokeWidth="1.2"
            fill="none"
            strokeLinecap="round"
          />
        </>
      );

    case 'facebook':
      // The f, built from three rectangles so the shape cannot come out garbled.
      return (
        <>
          <circle cx="12" cy="12" r="11" fill="#1877F2" />
          <rect x="10.8" y="8.2" width="2.4" height="11" fill="#fff" />
          <rect x="13.2" y="5.2" width="2.3" height="3.4" fill="#fff" />
          <rect x="9.3" y="11.4" width="5.3" height="2.2" fill="#fff" />
        </>
      );

    case 'instagram':
      return (
        <>
          <rect x="2" y="2" width="20" height="20" rx="5.5" fill="#E4405F" />
          <rect
            x="6.2"
            y="6.2"
            width="11.6"
            height="11.6"
            rx="3.4"
            fill="none"
            stroke="#fff"
            strokeWidth="1.7"
          />
          <circle cx="12" cy="12" r="3.1" fill="none" stroke="#fff" strokeWidth="1.7" />
          <circle cx="16.4" cy="7.6" r="1.15" fill="#fff" />
        </>
      );

    case 'youtube':
      return (
        <>
          <rect x="1.5" y="4.8" width="21" height="14.4" rx="4.2" fill="#FF0000" />
          <path d="M10.2 9.3l5.3 2.7-5.3 2.7z" fill="#fff" />
        </>
      );

    case 'discourse':
      // A speech bubble with three dots: a forum, not a social network.
      return (
        <>
          <rect x="2" y="2" width="20" height="20" rx="5.5" fill="#2b2b2b" />
          <path d="M6 17.6V8.2A2.2 2.2 0 0 1 8.2 6h7.6A2.2 2.2 0 0 1 18 8.2v5.4a2.2 2.2 0 0 1-2.2 2.2H9.4z" fill="#fff" />
          <circle cx="9.8" cy="11" r="1.05" fill="#2b2b2b" />
          <circle cx="12" cy="11" r="1.05" fill="#2b2b2b" />
          <circle cx="14.2" cy="11" r="1.05" fill="#2b2b2b" />
        </>
      );

    case 'hackernews':
      return (
        <>
          <rect x="2" y="2" width="20" height="20" rx="3" fill="#FF6600" />
          <path d="M8.1 7.2h2.1L12 10.4l1.8-3.2h2.1l-2.9 4.8v4.8h-2v-4.8z" fill="#fff" />
        </>
      );

    case 'stackexchange':
      return (
        <>
          <rect x="2" y="2" width="20" height="20" rx="4" fill="#3B78C0" />
          <rect x="6.4" y="12.6" width="11.2" height="1.8" rx="0.9" fill="#fff" />
          <rect x="6.4" y="9.4" width="11.2" height="1.8" rx="0.9" fill="#fff" />
          <rect x="8.6" y="6.2" width="9" height="1.8" rx="0.9" fill="#fff" />
        </>
      );

    case 'rss':
      return (
        <>
          <circle cx="12" cy="12" r="11" fill="#F26522" />
          <circle cx="8.2" cy="15.8" r="1.9" fill="#fff" />
          <path
            d="M8.2 10.4a5.4 5.4 0 0 1 5.4 5.4"
            stroke="#fff"
            strokeWidth="2.1"
            fill="none"
            strokeLinecap="round"
          />
          <path
            d="M8.2 5.6a10.2 10.2 0 0 1 10.2 10.2"
            stroke="#fff"
            strokeWidth="2.1"
            fill="none"
            strokeLinecap="round"
          />
        </>
      );

    case 'email':
      return (
        <>
          <circle cx="12" cy="12" r="11" fill="#6b7280" />
          <rect x="6.4" y="8.4" width="11.2" height="7.2" rx="1.4" fill="none" stroke="#fff" strokeWidth="1.6" />
          <path d="M6.9 9.3 12 13l5.1-3.7" stroke="#fff" strokeWidth="1.6" fill="none" strokeLinecap="round" />
        </>
      );

    default:
      // A platform we have no mark for. Neutral on purpose: better an obvious gap than a wrong
      // logo.
      return (
        <>
          <circle cx="12" cy="12" r="11" fill="#dcdcd8" />
          <circle cx="12" cy="12" r="3.4" fill="#fff" />
        </>
      );
  }
}
